/**
 * The browser half of notifications: registering the worker, asking for
 * permission, and keeping this device's subscription current.
 *
 * The one rule that governs the shape of this file: **`requestPermission()` must
 * be called synchronously from a click.** Safari grants a click a brief window
 * of "transient activation" and revokes it the moment you `await` anything, so
 * `enablePush()` asks for permission first and does its network work after. Get
 * that backwards and the iOS permission sheet silently never appears.
 */

import {
  deletePushSubscription,
  getPushPublicKey,
  putPushSubscription,
} from './api.ts';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === '1';

/** Remembers that this device opted in, so a dropped subscription can be quietly rebuilt. */
const OPT_IN_KEY = 'the-book:push-opt-in';

export function swSupported(): boolean {
  return !USE_MOCK && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/** iOS below 16.4 simply has no PushManager — feature-detect, never sniff versions. */
export function pushSupported(): boolean {
  return swSupported() && 'PushManager' in window && 'Notification' in window;
}

/** Running from the home screen rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac; the touch points give it away.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** Chrome/Firefox/Edge on iOS can't install anything — only Safari can. */
export function isIosNonSafari(): boolean {
  return isIos() && /CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
}

export function permissionState(): NotificationPermission | 'unsupported' {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Let the worker steer the page. When a notification is tapped and the app is
 * already open, the worker asks the page to navigate rather than reloading it
 * (which iOS standalone ignores anyway, and which would throw away the app's
 * state everywhere else). The page acks on the message's port so the worker
 * knows it was heard and doesn't fall back to a reload on top of it.
 */
export function onWorkerNavigate(handler: (path: string) => void): () => void {
  if (!swSupported()) return () => undefined;
  const listener = (event: MessageEvent) => {
    const data = event.data as { type?: unknown; url?: unknown } | null;
    if (!data || data.type !== 'navigate' || typeof data.url !== 'string') return;
    let target: URL;
    try {
      target = new URL(data.url, window.location.origin);
    } catch {
      return;
    }
    if (target.origin !== window.location.origin) return;
    handler(target.pathname + target.search);
    event.ports[0]?.postMessage({ ok: true });
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}

let updateCheckedAt = 0;

/**
 * Register the worker. Production only: a service worker in front of the Vite
 * dev server is a debugging tax nobody asked for.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!swSupported() || !import.meta.env.PROD) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      // Never let the HTTP cache decide whether we see a new worker.
      updateViaCache: 'none',
    });

    // This is a single-page app: a phone can hold the tab for days without a
    // navigation, and the browser's own update check won't fire. Look for a new
    // worker when the app comes back to the foreground, at most hourly.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - updateCheckedAt < 3_600_000) return;
      updateCheckedAt = Date.now();
      void registration.update().catch(() => {});
    });

    return registration;
  } catch (err) {
    console.warn('[push] service worker registration failed', err);
    return null;
  }
}

/**
 * The active registration, or null.
 *
 * Never await `navigator.serviceWorker.ready` directly: with nothing registered
 * it neither resolves nor rejects, so a caller waits forever. That happens in
 * dev (we don't register), and in production whenever registration failed —
 * private browsing, blocked site data — which is exactly when a button that
 * hangs on "Turning on…" with no error is most confusing.
 */
async function activeRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!swSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) return null;
  if (existing.active) return existing;
  // Registered but still installing: `ready` will settle shortly, but cap the
  // wait so a worker that never activates can't wedge the UI either.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000)),
  ]);
}

/**
 * The VAPID key arrives base64url; PushManager wants bytes. Backed by an
 * explicit ArrayBuffer because that's the only view type applicationServerKey
 * accepts.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export type EnableResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'unsupported' | 'denied' | 'not-configured' | 'no-worker' | 'failed';
      detail?: string;
    };

/**
 * Turn notifications on for this device. Call it straight from a click handler.
 */
export async function enablePush(): Promise<EnableResult> {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };

  // First, and before any await: the permission prompt.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  try {
    const { publicKey } = await getPushPublicKey();
    if (!publicKey) return { ok: false, reason: 'not-configured' };

    const registration = await activeRegistration();
    if (!registration) return { ok: false, reason: 'no-worker' };
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        // Required, and true to the truth: every push we send shows something.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    await putPushSubscription(subscription.toJSON());
    localStorage.setItem(OPT_IN_KEY, '1');
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: 'failed', detail: (err as Error)?.message };
  }
}

/** Turn this device off, both here and on the server. */
export async function disablePush(): Promise<void> {
  localStorage.removeItem(OPT_IN_KEY);
  const registration = await activeRegistration();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await deletePushSubscription(subscription.endpoint).catch(() => {});
  await subscription.unsubscribe().catch(() => {});
}

/** Is this device currently subscribed? */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const registration = await activeRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

/**
 * Housekeeping on every boot.
 *
 * Browsers drop push subscriptions on their own — iOS especially, after an
 * uninstall or a long quiet spell — and `pushsubscriptionchange` is unimplemented
 * in Safari and unreliable elsewhere. Rather than build a rotation endpoint the
 * worker couldn't authenticate anyway, we re-check here: refresh what we have,
 * and silently rebuild it if this device had opted in and lost it.
 */
export async function syncSubscriptionOnBoot(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try {
    const registration = await activeRegistration();
    if (!registration) return;
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await putPushSubscription(existing.toJSON());
      return;
    }
    if (localStorage.getItem(OPT_IN_KEY) !== '1') return;
    await enablePush();
  } catch {
    // Never let boot housekeeping break the app.
  }
}

/** Dev-only escape hatch: a worker on localhost outlives the project that made it. */
export async function unregisterServiceWorker(): Promise<void> {
  if (!swSupported()) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((r) => r.unregister()));
}
